<template>
    <DsModal
        show-close
        :is-open="isOpen"
        :title="$t('email.broadcast.canSpam.title')"
        class="can-spam-modal"
        @close="$emit('close')"
    >
        <template #default>
            <p class="instructions-message">
                {{ $t('email.broadcast.canSpam.instructions') }}
                <template>
                    <a
                        rel="noreferrer noopener"
                        target="_blank"
                        href="https://www.ftc.gov/tips-advice/business-center/guidance/can-spam-act-compliance-guide-business"
                    >
                        {{ $t('email.broadcast.canSpam.canSpamLink') }}
                        <DsIcon name="external-link" />
                    </a>
                </template>
            </p>

            <form
                v-if="countryOptions.length"
                ref="canSpamForm"
                novalidate
                @submit.prevent="handleSubmit"
            >
                <DsMultiselect
                    v-model="profile.companyCountry"
                    class="can-spam-country-input"
                    data-qa="can-spam-companyCountry-input"
                    bind-value-only
                    searchable
                    required
                    :options="countryOptions"
                    :submitted="submitted"
                    :placeholder="$t('forms.countryCode')"
                    name="country"
                    @input="loadRegionOptions"
                >
                    <template #help>
                        <small v-if="!profile.companyCountry && showInvalidFields">
                            {{ $t('forms.error.country') }}
                        </small>
                    </template>
                </DsMultiselect>

                <DsInputField
                    v-model="profile.companyStreetAddress1"
                    class="can-spam-address1-input"
                    data-qa="can-spam-companyStreetAddress1-input"
                    required
                    type="text"
                    name="address1"
                    :label="$t('forms.address1')"
                    :submitted="submitted"
                    trim
                >
                    <template #error>
                        <small v-if="!profile.companyStreetAddress1 && showInvalidFields">
                            {{ $t('forms.error.address1') }}
                        </small>
                    </template>
                </DsInputField>

                <DsInputField
                    v-model="profile.companyStreetAddress2"
                    class="can-spam-address2-input"
                    type="text"
                    name="address2"
                    :label="$t('email.broadcast.canSpam.address2')"
                    :submitted="submitted"
                    trim
                />

                <DsMultiselect
                    v-model="profile.companyState"
                    class="can-spam-state-input"
                    bind-value-only
                    searchable
                    name="region"
                    :readonly="!regionOptions.length && !loadingRegionOptions"
                    :options="regionOptions"
                    :submitted="submitted"
                    :placeholder="$t('forms.region')"
                    :loading="loadingRegionOptions"
                />

                <DsInputField
                    v-model="profile.companyPostalCode"
                    class="can-spam-postal-code-input"
                    data-qa="can-spam-companyPostalCode-input"
                    required
                    type="text"
                    name="postal-code"
                    :label="$t('forms.postalCodeGeneric')"
                    :submitted="submitted"
                    trim
                >
                    <template #error>
                        <small v-if="!profile.companyPostalCode && showInvalidFields">
                            {{ $t('forms.error.postalCodeGeneric') }}
                        </small>
                    </template>
                </DsInputField>

                <DsInputField
                    v-model="profile.companyCity"
                    class="can-spam-city-input"
                    data-qa="can-spam-companyCity-input"
                    required
                    type="text"
                    name="locality"
                    :label="$t('forms.locality')"
                    :submitted="submitted"
                    trim
                >
                    <template #error>
                        <small v-if="!profile.companyCity && showInvalidFields">
                            {{ $t('forms.error.locality') }}
                        </small>
                    </template>
                </DsInputField>

                <div class="button-row">
                    <DsFilledButton
                        type="submit"
                        data-qa="can-spam-continue-button"
                        :loading="isSaving"
                    >
                        {{ $t('global.continue') }}
                    </DsFilledButton>

                    <DsOutlineButton
                        data-qa="can-spam-cancel-button"
                        @click="$emit('close')"
                    >
                        {{ $t('global.cancel') }}
                    </DsOutlineButton>
                </div>
            </form>

            <div v-else class="spinner-container">
                <DsSpinner />
            </div>
        </template>
    </DsModal>
</template>

<script>
import clonedeep from 'lodash.clonedeep';
import {
    getCountryOptions,
    getStateMapForCountry,
    saveMarketingSettingsInformation,
} from '@/api/broadcasts/emailBroadcast';

export default {
    props: {
        companyProfile: {
            type: Object,
            required: true,
        },
        isOpen: Boolean,
    },

    data() {
        return {
            submitted: false,
            loadingRegionOptions: false,
            profile: clonedeep(this.companyProfile),
            showInvalidFields: false,
            isSaving: false,
            countryOptions: [],
            regionOptions: [],
        };
    },

    created() {
        this.loadCountryOptions();
    },

    watch: {
        companyProfile(newVal) {
            this.profile = clonedeep(newVal);
            this.loadRegionOptions();
        },
    },

    methods: {
        async loadCountryOptions() {
            try {
                this.countryOptions = await getCountryOptions();
            } catch (e) {
                this.$error({ message: this.$t('email.broadcast.canSpam.errors.country'), bottom: true });
            }
        },

        async loadRegionOptions() {
            const { companyCountry } = this.profile;

            this.loadingRegionOptions = true;

            try {
                this.regionOptions = companyCountry
                    ? await getStateMapForCountry({ country: companyCountry })
                    : [];
            } catch (e) {
                this.$error({ message: this.$t('email.broadcast.canSpam.errors.state'), bottom: true });
            }

            this.loadingRegionOptions = false;
        },

        async handleSubmit() {
            this.submitted = true;

            if (!this.$refs.canSpamForm.checkValidity()) {
                this.showInvalidFields = true;

                return;
            }

            this.showInvalidFields = false;
            this.isSaving = true;

            try {
                const { profile } = this;

                await saveMarketingSettingsInformation({ profile });

                this.$toast({ message: this.$t('email.broadcast.canSpam.canSpamSaved') });
                this.$emit('close', profile);
            } catch (error) {
                this.$error({
                    message: this.$t('email.broadcast.canSpam.savingErrorMessage'),
                });
                this.$refs.input.reportValidity();
            }

            this.submitted = false;
            this.isSaving = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.can-spam-modal {
    --modal-padding: #{$spacing-200};
}

.can-spam-address1-input,
.can-spam-address2-input,
.can-spam-city-input,
.can-spam-postal-code-input,
.can-spam-country-input,
.can-spam-state-input {
    --input-font-size: #{$font-size-sm};
}

.instructions-message {
    font-size: $font-size-md;
    --icon-size: #{px-to-rem(14px)};
}

.button-row {
    justify-content: flex-start;
}

.can-spam-country-input, .can-spam-state-input {
    --input-padding-top: 0;
    --input-padding-bottom: 0;
}
</style>
